import { breadcrumbJsonLd } from "@/lib/site";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeading, Eyebrow, TrustBar, CtaBand } from "@/components/sections";
import { inspectorImg } from "@/lib/inspector-image";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Anchor Services — Sarasota Complete Property Care" },
      { name: "description", content: "Locally owned in Sarasota, Anchor Services handles home watch, cleanouts, hauling, removals and concierge work for homeowners, investors and property managers. Licensed, insured and communicative." },
      { property: "og:title", content: "About Anchor Services" },
      { property: "og:description", content: "Locally owned Sarasota property care — home watch, cleanouts, hauling and concierge, all from one trustworthy, detail-obsessed team." },
      { property: "og:url", content: "https://anchorsrq.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://anchorsrq.com/about" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd([{ name: 'Home', url: 'https://anchorsrq.com/' }, { name: 'About Anchor Services', url: 'https://anchorsrq.com/about' }])) },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <>
      <Section>
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>About Anchor Services</Eyebrow>
            <h1 className="font-serif text-4xl sm:text-5xl">The trusted name in Sarasota property care.</h1>
            <p className="mt-5 text-muted-foreground">
              Anchor Services was founded by Sarasota locals who kept seeing the same problem from every angle: homeowners juggling one vendor to check on the house, another to haul junk, someone else to clear out a property before a sale. Every handoff meant another phone call, another no-show, another thing to worry about.
            </p>
            <p className="mt-4 text-muted-foreground">
              So we built one local team that does all of it — home watch, cleanouts, hauling, furniture and appliance removal, storm debris and concierge work — with the same consistency, communication and craft on every job, no matter which service you call about.
            </p>
          </div>
          <img src={inspectorImg.src} srcSet={inspectorImg.srcSet} sizes={inspectorImg.sizes} alt="Anchor Services team member at a Sarasota property" width={inspectorImg.width} height={inspectorImg.height} loading="lazy" decoding="async" className="aspect-[16/9] w-full rounded-2xl object-cover shadow-lg" />
        </div>
      </Section>

      <TrustBar />

      <Section>
        <div className="mx-auto grid max-w-5xl gap-10 sm:grid-cols-2">
          {[
            { t: "Our Mission", d: "To be the one call Sarasota property owners make for anything their home needs — treating every property with the same care we'd want for our own." },
            { t: "Why locals trust us", d: "We answer the phone. We arrive when we say we will. We send a report after every visit. And we tell the truth — even when it's not what you want to hear." },
            { t: "Licensed & Insured", d: "Anchor Services is fully licensed, insured and bonded in the state of Florida. Every team member is background checked before entering a client home." },
            { t: "Communication promise", d: "Detailed photo reports after every visit. Real-time alerts for anything urgent. One point of contact who actually knows your property." },
          ].map((b) => (
            <div key={b.t} className="rounded-xl border border-border bg-card p-6">
              <div className="font-serif text-xl text-primary">{b.t}</div>
              <p className="mt-3 text-sm text-muted-foreground">{b.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Link to="/" hash="services" className="text-sm font-medium text-primary underline underline-offset-4">
            Explore all of our services →
          </Link>
        </div>
      </Section>

      <CtaBand title="Ready to put your Sarasota home in good hands?" sub="Schedule a complimentary walkthrough today." />
    </>
  );
}